// folio.js — the "Index Reel": a black, pinned stage where portfolio images
// fall from the top, focus at the exact centre, and sink past the bottom as
// you scroll. Left/right index columns light the active work; side captions
// fade in on the centred image; a snap locks each work to the middle. All ten
// must pass before the pin releases into Services.
//
// Rather than translate one long track, folio.js positions EACH item every
// frame from a single 0..1 progress — this owns the fall direction, the
// centre-focus depth, the velocity stretch and the caption reveal in one loop.
//
// Mobile / reduced-motion skip this entirely; CSS stacks the works vertically.
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function initFolio() {
  const section = document.querySelector('[data-folio]');
  const sticky  = document.querySelector('[data-folio-sticky]');
  const reel    = document.querySelector('[data-folio-reel]');
  if (!section || !sticky || !reel) return;

  const mobile  = matchMedia('(max-width: 900px)').matches;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  const items = [...reel.querySelectorAll('[data-folio-item]')];
  const bar = section.querySelector('[data-folio-bar]');
  const copyPanel = section.querySelector('[data-folio-copy]');
  const tickerPanel = section.querySelector('[data-folio-ticker]');
  const N = items.length;
  if (!N) return;

  if (mobile || reduced) {
    sticky.style.clipPath = 'none';
    return;
  }

  const last = N - 1;
  const spacing = () => (items[0]?.offsetHeight || window.innerHeight * 0.72) + 30;

  // ── right rail: read every work's own metadata straight off the reel ──
  // Generated, never hand-written, so the ticker can't drift out of sync
  // with the images it labels.
  const meta = items.map((el, i) => {
    const sub = (el.querySelector('.folioItem__subtitle')?.textContent || '').split('·');
    return {
      no: String(i + 1).padStart(2, '0'),
      title: (el.querySelector('.folioItem__title')?.textContent || '').trim(),
      cat: (sub[0] || '').trim(),
      year: (sub[1] || '').trim(),
      ratio: (el.style.getPropertyValue('--ratio') || '').split('/').map((x) => x.trim()).filter(Boolean).join(':'),
    };
  });

  // inner layers — the width squeeze lives on the box, the zoom on the image,
  // and the caption counter-scales so type never distorts with the crop
  const boxes = items.map((el) => el.querySelector('.imgBox'));
  const imgs  = items.map((el) => el.querySelector('.imgBox img'));
  const caps  = items.map((el) => el.querySelector('.folioItem__caption'));

  const tickerList = section.querySelector('[data-folio-ticker-list]');
  if (tickerList) {
    tickerList.innerHTML = meta
      .map((m, i) => `<li class="folioTicker__row" data-folio-idx="${i}"><b>${m.no}/</b><span>${m.title}</span></li>`)
      .join('');
  }
  const numeralEl = section.querySelector('[data-folio-numeral]');
  const readoutEl = section.querySelector('[data-folio-readout]');
  const catEl     = section.querySelector('[data-folio-cat]');
  const yearEl    = section.querySelector('[data-folio-year]');
  const ratioEl   = section.querySelector('[data-folio-ratio]');
  const totalEl   = section.querySelector('[data-folio-total]');
  if (totalEl) totalEl.textContent = '/' + String(N).padStart(2, '0');

  // row height drives the ticker travel — measured, not assumed
  let rowH = 0;
  const measureRow = () => {
    rowH = tickerList?.firstElementChild?.getBoundingClientRect().height || 0;
  };
  measureRow();

  const idxEls = [...section.querySelectorAll('[data-folio-idx]')];

  // per-frame state, written by the pin's onUpdate, consumed by the ticker
  const state = { progress: 0, vel: 0 };
  let curNearest = -1;        // nearest item (index highlight) — always one lit
  let curFocus = -1;          // genuinely centred item (side label) — may be none
  let dirty = true;
  let velSmooth = 0;          // eased scroll velocity, drives the numeral shear

  // ── pin + scrub + snap ───────────────────────────────────────────────
  gsap.fromTo(sticky,
    { clipPath: 'inset(100% 0 0 0)' },
    {
      clipPath: 'inset(0% 0 0 0)',
      ease: 'none',
      scrollTrigger: { trigger: section, start: 'top bottom', end: 'top top', scrub: true },
    }
  );

  const st = ScrollTrigger.create({
    trigger: section,
    start: 'top top',
    end: () => '+=' + N * window.innerHeight * 0.85,
    pin: true,
    anticipatePin: 1,
    scrub: true,
    invalidateOnRefresh: true,
    onRefresh: () => { measureRow(); dirty = true; },
    onUpdate: (self) => {
      state.progress = self.progress; state.vel = self.getVelocity(); dirty = true;
    },
  });

  // ── the render loop — always on so the stretch can decay when idle ───
  const clamp = gsap.utils.clamp;
  // ── shape of the width move ─────────────────────────────────────────
  // NARROW  · how far the frame is squeezed while a work is in transit
  // ZOOM    · how far the picture is pushed in inside that narrow frame
  // The box scales in X only; the image counter-scales by 1/sx so the crop
  // moves, not the picture — a curtain, not a stretch. The small overshoot
  // in easeOutBack is what reads as the "unfold" at the centre.
  const NARROW = .58, ZOOM = 1.3;
  const smooth = (t) => t * t * (3 - 2 * t);
  const outCubic = (t) => 1 - Math.pow(1 - t, 3);
  const outBack = (t) => { const c = 1.22, u = t - 1; return 1 + (c + 1) * u * u * u + c * u * u; };
  const barTrackEl = section.querySelector('.folioTicker__axis');
  const barTravel = () => (barTrackEl ? barTrackEl.offsetHeight - (bar ? bar.offsetHeight : 0) : 0);
  const render = () => {
    // ease the velocity stretch toward its target, mark dirty until settled.
    // state.vel only refreshes while scrolling, so decay it here — otherwise
    // the numeral would freeze mid-shear the moment the scroll stops.
    const velTarget = clamp(-1, 1, state.vel / 2600);
    velSmooth += (velTarget - velSmooth) * .14;
    state.vel *= .88;
    const settling = Math.abs(velSmooth) > .0015;

    if (!dirty && !settling) return;
    dirty = false;

    const S = spacing();
    const active = state.progress * N - 1;
    const nearest = clamp(0, last, Math.round(active));
    // captions only when an image is genuinely at the centre, not mid-transit
    const focus = active >= 0 && Math.abs(active - nearest) < 0.14 ? nearest : -1;

    for (let i = 0; i < N; i++) {
      const rel = active - i;              // >0 → past centre (below); <0 → above
      const d = Math.min(Math.abs(rel), 2);
      const y = rel * S;
      const reveal = clamp(0, 1, rel + 1);
      const el = items[i];
      el.style.transform =
        `translate(-50%, -50%) translateY(${y.toFixed(1)}px)`;
      el.style.opacity = reveal.toFixed(3);
      el.style.clipPath = `inset(${((1 - reveal) * 100).toFixed(2)}% 0 0 0)`;
      el.style.filter = 'none';
      el.style.zIndex = String(Math.round(100 - d * 10));

      // ── width squeeze → unfold, with the picture zooming out inside it ──
      // near: 0 far from centre → 1 dead centre. Three curves off one value,
      // deliberately out of step, so the frame, the picture and the caption
      // arrive in that order instead of snapping together.
      const near = 1 - Math.min(Math.abs(rel), 1);
      const sx = clamp(.2, 1.06,
        (NARROW + (1 - NARROW) * outBack(near)) * (1 - Math.abs(velSmooth) * .07));
      const zoom = ZOOM - (ZOOM - 1) * outCubic(near);
      const box = boxes[i], img = imgs[i], cap = caps[i];
      if (box) box.style.transform = `scaleX(${sx.toFixed(4)})`;
      if (img) {
        // 1/sx undoes the frame's squeeze; the leftover drift adds depth
        img.style.transform =
          `translate3d(0, ${(rel * -2.2).toFixed(2)}%, 0) scale3d(${(zoom / sx).toFixed(4)}, ${zoom.toFixed(4)}, 1)`;
      }
      if (cap) {
        cap.style.transform = `scaleX(${(1 / sx).toFixed(4)})`;
        cap.style.opacity = (.06 + .94 * smooth(near)).toFixed(3);
      }
    }

    // index highlight — always one row lit, tracks the nearest item every frame.
    // The folioCopy panel now holds fixed text, so nothing is swapped here
    // beyond the active-row highlight.
    if (nearest !== curNearest) {
      idxEls.forEach((el) => el.classList.toggle('is-on', Number(el.dataset.folioIdx) === nearest));
      if (numeralEl) numeralEl.textContent = meta[nearest].no;
      curNearest = nearest;
    }
    // side label + spec readout — only on the item actually centred, so the
    // rail goes quiet mid-transit instead of flickering through values
    if (focus !== curFocus) {
      items.forEach((el, i) => el.classList.toggle('is-focus', i === focus));
      if (readoutEl) {
        readoutEl.classList.toggle('is-on', focus >= 0);
        if (focus >= 0) {
          const m = meta[focus];
          if (catEl)   catEl.textContent   = m.cat;
          if (yearEl)  yearEl.textContent  = m.year;
          if (ratioEl) ratioEl.textContent = m.ratio ? 'RATIO ' + m.ratio : '';
        }
      }
      curFocus = focus;
    }

    if (bar) bar.style.transform = `translateY(${(state.progress * barTravel()).toFixed(1)}px)`;

    // ── right rail ──────────────────────────────────────────────────────
    // 1 · the ticker rides the same float as the images, so the centred row
    //     and the centred image are always on one horizontal line.
    if (tickerList && rowH) {
      tickerList.style.transform = `translateY(${(-active * rowH).toFixed(2)}px)`;
    }
    // 2 · the numeral shears with scroll velocity and snaps crisp when idle
    if (numeralEl) {
      numeralEl.style.transform =
        `skewY(${(velSmooth * -5).toFixed(2)}deg) scaleY(${(1 + Math.abs(velSmooth) * .1).toFixed(3)})`;
    }
  };

  gsap.ticker.add(render);
  render();

  if (matchMedia('(pointer: fine)').matches) {
    let targetX = 0, targetY = 0, currentX = 0, currentY = 0, parallaxRaf = null;
    sticky.addEventListener('pointermove', (event) => {
      const rect = sticky.getBoundingClientRect();
      targetX = ((event.clientX - rect.left) / rect.width - .5) * 2;
      targetY = ((event.clientY - rect.top) / rect.height - .5) * 2;
      if (!parallaxRaf) parallaxRaf = requestAnimationFrame(updateParallax);
    });
    sticky.addEventListener('pointerleave', () => {
      targetX = 0;
      targetY = 0;
      if (!parallaxRaf) parallaxRaf = requestAnimationFrame(updateParallax);
    });
    function updateParallax() {
      currentX += (targetX - currentX) * .055;
      currentY += (targetY - currentY) * .055;
      reel.style.transform = `translate3d(${(currentX * 10).toFixed(2)}px, ${(currentY * 7).toFixed(2)}px, 0)`;
      if (copyPanel) copyPanel.style.transform = `translate3d(${(-currentX * 7).toFixed(2)}px, ${(-currentY * 5).toFixed(2)}px, 0)`;
      if (tickerPanel) tickerPanel.style.transform = `translate3d(${(currentX * 11).toFixed(2)}px, ${(currentY * 7).toFixed(2)}px, 0)`;
      parallaxRaf = Math.abs(targetX - currentX) > .002 || Math.abs(targetY - currentY) > .002
        ? requestAnimationFrame(updateParallax) : null;
    }
  }

  // ── clickable index → jump to that work's centre ─────────────────────
  idxEls.forEach((el) => {
    el.addEventListener('click', () => {
      const i = Number(el.dataset.folioIdx);
      const p = i / last;
      const top = st.start + p * (st.end - st.start);
      window.scrollTo({ top, behavior: 'smooth' });
    });
  });

  window.addEventListener('load', () => ScrollTrigger.refresh());

  // dev handle (matches window.__stage / __about used elsewhere)
  window.__folio = { st, items, render, state };
}
